import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Post from './Post';
import PostSkeleton from './PostSkeleton';
import { PostProps } from '../../../../types/postType';
import { UserType } from '../../../../types/userType';

interface RepliesSectionProps {
  replies: PostProps[];
  totalReplies: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  currentUser: UserType | null;
  postType?: string;
  onPostDeleted: (deletedPostId: number) => void;
  onIntersection: () => void;
}

const RepliesSection: React.FC<RepliesSectionProps> = ({
  replies,
  totalReplies,
  isLoading,
  isLoadingMore,
  hasMore,
  currentUser,
  postType = 'post',
  onPostDeleted,
  onIntersection
}) => {
  const navigate = useNavigate();
  const observerTarget = useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onIntersection();
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onIntersection]);

  if (isLoading) {
    return (
      <div className="replies-section">
        <h2 className="replies-section-title">Replies</h2>
        <PostSkeleton />
        <PostSkeleton />
      </div>
    );
  }

  if (replies.length === 0) {
    return null;
  }

  return (
    <div className="replies-section">
      <h2 className="replies-section-title">
        {postType === 'collab' ? 'Applies' : 'Replies'} ({totalReplies})
      </h2>
      {replies.map((reply) => (
        <Post 
          key={reply.id}
          {...reply}
          currentUser={currentUser}
          currentUserId={currentUser?.id?.toString() ?? ''}
          onPostDeleted={onPostDeleted}
          isNewPost={false}
          onReplyClick={() => {}}
          onClick={() => navigate(`/posts/${reply.slug}`)}
        />
      ))}
      {hasMore && (
        <div ref={observerTarget} className="loading-more">
          {isLoadingMore && (
            <div className="loading-spinner">
              {/* Loading spinner */}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RepliesSection; 